import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Building2, Upload, AlertTriangle, Package, History, Loader2, Send, FileText } from 'lucide-react';
import type { User } from '../App';
import { getInstitutionalDashboard, submitSlotRequest, type InstitutionalDashboardData } from '../api/institutions';
import { listSubmissions, requestFilingDocumentationPrep, type InnovationSubmission } from '../api/innovations';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

interface InstitutionalAdminDashboardProps {
  user: User;
  onNavigateToBulkUpload?: () => void;
}

export function InstitutionalAdminDashboard({ user, onNavigateToBulkUpload }: InstitutionalAdminDashboardProps) {
  const [data, setData] = useState<InstitutionalDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [slotRequestSlots, setSlotRequestSlots] = useState('50');
  const [slotRequestNotes, setSlotRequestNotes] = useState('');
  const [slotRequestSubmitting, setSlotRequestSubmitting] = useState(false);
  const [slotRequestError, setSlotRequestError] = useState<string | null>(null);
  const [recentSubmissions, setRecentSubmissions] = useState<InnovationSubmission[]>([]);
  const [filingDocRequestId, setFilingDocRequestId] = useState<string | null>(null);

  const fetchDashboard = () => {
    setLoading(true);
    getInstitutionalDashboard()
      .then(setData)
      .catch((e: any) => setError(e?.message || 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getInstitutionalDashboard(),
      listSubmissions(1, 80).then((r) => r.submissions).catch(() => []),
    ])
      .then(([d, subs]) => {
        if (!cancelled) {
          setData(d);
          setRecentSubmissions(subs || []);
        }
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.message || 'Failed to load dashboard');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmitSlotRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    const num = parseInt(slotRequestSlots, 10);
    if (isNaN(num) || num < 1) {
      setSlotRequestError('Enter a valid number (at least 1).');
      return;
    }
    setSlotRequestSubmitting(true);
    setSlotRequestError(null);
    try {
      await submitSlotRequest(num, slotRequestNotes.trim() || undefined);
      setSlotRequestSlots('50');
      setSlotRequestNotes('');
      fetchDashboard();
    } catch (e: any) {
      setSlotRequestError(e?.response?.data?.message || e?.message || 'Request failed');
    } finally {
      setSlotRequestSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--gold)]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-700">{error || 'Unable to load institution data.'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const inst = data.institution;
  const slotsRemaining = inst.slots_remaining ?? 0;
  const slotsAllocated = inst.slots_allocated || 0;
  const isSuspended = inst.status === 'suspended';
  const isExhausted = slotsRemaining === 0;
  const isLow = slotsRemaining > 0 && slotsRemaining < 20;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Building2 className="w-8 h-8 text-[var(--gold)]" />
        <div>
          <h1 className="text-2xl font-display font-semibold text-[var(--navy)]">
            {inst.name} {inst.short_name ? `(${inst.short_name})` : ''}
          </h1>
          <p className="text-[var(--navy)]/70">Institutional dashboard</p>
        </div>
      </div>

      {data.warnings && data.warnings.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6 flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <ul className="text-amber-800 text-sm list-disc list-inside">
              {data.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-[rgba(201,168,76,0.25)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[var(--navy)]/80">Slot balance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[var(--navy)]">{slotsRemaining}</div>
            <p className="text-sm text-[var(--navy)]/70">of {slotsAllocated} allocated</p>
            <div className="mt-2 h-2 bg-[var(--gold-pale)] rounded-full overflow-hidden">
              <div
                className="h-full bg-[var(--gold)] rounded-full transition-all"
                style={{ width: `${slotsAllocated ? Math.min(100, (slotsRemaining / slotsAllocated) * 100) : 0}%` }}
              />
            </div>
            <Badge
              className="mt-2"
              variant={isSuspended ? 'destructive' : isExhausted ? 'destructive' : isLow ? 'secondary' : 'default'}
            >
              {isSuspended ? 'Suspended' : isExhausted ? 'Exhausted' : isLow ? 'Low' : 'Healthy'}
            </Badge>
          </CardContent>
        </Card>

        <Card className="border-[rgba(201,168,76,0.25)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[var(--navy)]/80">Total submissions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[var(--navy)]">{inst.slots_used}</div>
            <p className="text-sm text-[var(--navy)]/70">IPs uploaded</p>
          </CardContent>
        </Card>

        <Card className="border-[rgba(201,168,76,0.25)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[var(--navy)]/80">Status</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge className="capitalize">{inst.status}</Badge>
            {inst.contract_end_date && (
              <p className="text-sm text-[var(--navy)]/70 mt-2">Contract ends: {new Date(inst.contract_end_date).toLocaleDateString()}</p>
            )}
          </CardContent>
        </Card>

        <Card className="border-[rgba(201,168,76,0.25)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[var(--navy)]/80">Bulk upload</CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              className="w-full bg-[var(--gold)] text-[var(--navy)] hover:opacity-90"
              disabled={isSuspended || isExhausted}
              onClick={onNavigateToBulkUpload}
            >
              <Upload className="w-4 h-4 mr-2" />
              Bulk upload innovations
            </Button>
            {(isSuspended || isExhausted) && (
              <p className="text-xs text-[var(--navy)]/70 mt-2">
                {isSuspended ? 'Account suspended.' : 'No slots remaining. Contact admin for renewal.'}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-[rgba(201,168,76,0.25)]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[var(--navy)]">Request more slots</CardTitle>
          <CardDescription>Submit a request for additional slots. A super admin will review and approve or reject it.</CardDescription>
        </CardHeader>
        <CardContent>
          {data.pending_slot_request ? (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
              <p className="text-amber-800 font-medium">You have a pending request</p>
              <p className="text-sm text-amber-700 mt-1">
                {data.pending_slot_request.requested_slots} slots requested on {new Date(data.pending_slot_request.requested_at).toLocaleDateString()}
                {data.pending_slot_request.notes && ` — ${data.pending_slot_request.notes}`}
              </p>
              <p className="text-sm text-amber-600 mt-2">Wait for super admin to approve. You can submit a new request after this one is processed.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmitSlotRequest} className="space-y-4 max-w-md">
              <div>
                <Label htmlFor="slot-request-count" className="text-[var(--navy)]">Number of slots requested</Label>
                <Input
                  id="slot-request-count"
                  type="number"
                  min={1}
                  value={slotRequestSlots}
                  onChange={(e) => setSlotRequestSlots(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="slot-request-notes" className="text-[var(--navy)]">Reason or notes (optional)</Label>
                <Textarea
                  id="slot-request-notes"
                  placeholder="e.g. Need capacity for Q2 batch of innovations"
                  value={slotRequestNotes}
                  onChange={(e) => setSlotRequestNotes(e.target.value)}
                  rows={2}
                  className="mt-1"
                />
              </div>
              {slotRequestError && <p className="text-sm text-red-600">{slotRequestError}</p>}
              <Button type="submit" disabled={slotRequestSubmitting} className="bg-[var(--gold)] text-[var(--navy)]">
                <Send className="w-4 h-4 mr-2" />
                {slotRequestSubmitting ? 'Submitting...' : 'Submit request'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>

      <Card className="border-[rgba(201,168,76,0.25)]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[var(--navy)]">
            <Package className="w-5 h-5" />
            Recent uploads
          </CardTitle>
          <CardDescription>Latest bulk upload batches</CardDescription>
        </CardHeader>
        <CardContent>
          {data.recent_uploads && data.recent_uploads.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Submissions</TableHead>
                  <TableHead>Slots used</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recent_uploads.map((u) => (
                  <TableRow key={u.batch_uuid}>
                    <TableCell>{new Date(u.upload_date).toLocaleString()}</TableCell>
                    <TableCell>{u.submission_count}</TableCell>
                    <TableCell>{u.slots_consumed}</TableCell>
                    <TableCell><Badge variant="secondary">{u.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-[var(--navy)]/70 text-sm">No uploads yet.</p>
          )}
        </CardContent>
      </Card>

      <Card className="border-[rgba(201,168,76,0.25)]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[var(--navy)]">
            <FileText className="w-5 h-5" />
            Recent submissions
          </CardTitle>
          <CardDescription>Innovations you submitted (single or bulk). Slot balance above updates when you submit.</CardDescription>
        </CardHeader>
        <CardContent>
          {recentSubmissions.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Filing documentation</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentSubmissions.map((s) => (
                  <TableRow key={s.submission_uuid}>
                    <TableCell className="font-medium text-[var(--navy)] max-w-[280px] truncate" title={s.title}>
                      {s.title}
                    </TableCell>
                    <TableCell className="text-[var(--navy)]/70 text-sm">
                      {s.submission_date ? new Date(s.submission_date).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={s.status === 'submitted' ? 'default' : 'secondary'} className="capitalize">
                        {s.status?.replace('_', ' ') ?? s.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {s.status === 'completed' && s.feedback_approval_status === 'approved' ? (
                        s.filing_documentation_prep?.package_url ? (
                          <span className="text-emerald-800 font-medium">Package ready</span>
                        ) : s.filing_documentation_prep?.requested_at ? (
                          <span className="text-[var(--navy)]/80">Requested — Insolip preparing</span>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-[var(--navy)]"
                            disabled={filingDocRequestId === s.submission_uuid}
                            onClick={async () => {
                              setFilingDocRequestId(s.submission_uuid);
                              try {
                                const res = await requestFilingDocumentationPrep(s.submission_uuid);
                                setRecentSubmissions((prev) =>
                                  prev.map((row) =>
                                    row.submission_uuid === s.submission_uuid
                                      ? {
                                          ...row,
                                          filing_documentation_prep: {
                                            ...(row.filing_documentation_prep || {}),
                                            requested_at: new Date().toISOString(),
                                          },
                                        }
                                      : row
                                  )
                                );
                                alert(res.message || 'Request recorded.');
                              } catch (e: unknown) {
                                const msg =
                                  e && typeof e === 'object' && 'message' in e
                                    ? String((e as { message: unknown }).message)
                                    : 'Request failed';
                                alert(msg);
                              } finally {
                                setFilingDocRequestId(null);
                              }
                            }}
                          >
                            {filingDocRequestId === s.submission_uuid ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              'Request preparation'
                            )}
                          </Button>
                        )
                      ) : (
                        <span className="text-[var(--navy)]/50">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-[var(--navy)]/70 text-sm">No submissions yet. Use Bulk upload or submit as innovator.</p>
          )}
        </CardContent>
      </Card>

      <Card className="border-[rgba(201,168,76,0.25)]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[var(--navy)]">
            <History className="w-5 h-5" />
            Slot history
          </CardTitle>
          <CardDescription>Allocations, renewals, and consumption</CardDescription>
        </CardHeader>
        <CardContent>
          {data.slot_history && data.slot_history.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Change</TableHead>
                  <TableHead>Balance after</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.slot_history.map((h) => (
                  <TableRow key={h.history_uuid}>
                    <TableCell>{new Date(h.performed_at).toLocaleString()}</TableCell>
                    <TableCell><Badge variant="outline">{h.transaction_type}</Badge></TableCell>
                    <TableCell>{h.slots_changed > 0 ? `+${h.slots_changed}` : h.slots_changed}</TableCell>
                    <TableCell>{h.new_balance}</TableCell>
                    <TableCell className="text-[var(--navy)]/70">{h.notes || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-[var(--navy)]/70 text-sm">No slot history yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
